/**
 * Centralised navigation configuration for the Helix sidebar.
 *
 * Instead of four hardcoded arrays scattered inside Sidebar.tsx, every
 * navigation item is defined once in a registry (`NAV_ITEMS`) and then
 * referenced by key in the per-mode ordering arrays. The public
 * `buildNavigation()` function selects the right ordering at runtime.
 */

import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Settings,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Users,
  FileText,
  History,
  ClipboardList,
  Newspaper,
  UserCog,
  Upload,
  UserPlus,
  AlertTriangle,
  Truck,
  BarChart3,
  Building2,
  Table,
  LineChart,
  FolderOpen,
  AlertOctagon,
  Database,
  PieChart,
  Box,
  Factory,
  Package,
  CalendarClock,
  ClipboardCheck,
  Timer,
  Receipt,
  FileSignature,
} from 'lucide-react';
import type { MenuVisibility } from '../contexts/MenuSettingsContext';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FeatureFlag =
  | 'delivery_appeals'
  | 'spend_leakage'
  | 'bulk_upload'
  | 'commodity_codes'
  | 'supplier_stock_levels'
  | 'switch_call_off_tab'
  | 'po_line_schedules'
  | 'schedule_collaboration'
  | 'allow_supplier_decline'
  | 'spend_dashboard'
  | 'supplier_uploads'
  | 'division_scoring'
  | 'call_off_performance';

/** Shape consumed by the Sidebar rendering logic (unchanged from old code). */
export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  children?: NavItem[];
  visibilityKey?: keyof MenuVisibility;
  /**
   * Optional supplier-side override. When the user is viewing the supplier
   * sidebar, buildNavigation swaps `visibilityKey` → `supplierVisibilityKey`
   * so the item is gated by its own admin toggle instead of sharing state
   * with the customer-side key.
   */
  supplierVisibilityKey?: keyof MenuVisibility;
  tourId?: string;
  featureFlag?: FeatureFlag;
  /** Hide the item only when this feature flag is explicitly `false` (absent → shown). */
  hiddenWhenFlagFalse?: 'part_schedule';
  /** Alternate label when portal context is flipped (Customer Portal mode) */
  flipName?: string;
  /**
   * CR-6084: when true, the item is hidden unless the user is an admin
   * (Helix internal, customer admin, or supplier admin — i.e. the same
   * `canManageUsers` capability the Sidebar already computes from the user
   * object). Use for administrative / audit surfaces (User Activity History,
   * future audit logs) where exposing the menu item to plain users is itself
   * an information leak.
   */
  adminOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Item registry
// ---------------------------------------------------------------------------
// Each unique navigation entry is defined exactly once. Items that differ
// between standard and Barratt modes (different label, icon, etc.) are
// stored as separate keys (e.g. `suppliers` vs `barrattSupplier`).
// ---------------------------------------------------------------------------

const NAV_ITEMS = {
  // ---- Common ----
  dashboard: {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    visibilityKey: 'dashboard',
    supplierVisibilityKey: 'supplierDashboard',
    tourId: 'dashboard',
  },
  documents: {
    name: 'Documents',
    href: '/documents',
    icon: FileText,
    visibilityKey: 'documents',
    supplierVisibilityKey: 'supplierDocuments',
  },

  // ---- Standard customer ----
  supplierPerformance: {
    name: 'Supplier Performance',
    flipName: 'Customer Performance',
    href: '/suppliers/performance',
    icon: TrendingUp,
    visibilityKey: 'supplierPerformance',
    // SI-16: also on the supplier sidebar (own scores, one row per customer),
    // behind its own admin toggle like Dashboard / Documents.
    supplierVisibilityKey: 'supplierSelfPerformance',
  },
  reportsCustomer: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  /** Prebuilt spend analytics (invoice-value charts). Feature-flagged so
   *  only tenants with seeded spend dashboards surface the item. */
  spendDashboard: {
    name: 'Spend Dashboard',
    href: '/barratt/spend-data',
    icon: BarChart3,
    featureFlag: 'spend_dashboard' as FeatureFlag,
  },
  /** Delivery and quality from the monthly supplier files — the data that
   *  runs past the invoice ledger behind the spend charts. */
  barrattSupplierPerformance: {
    name: 'Supplier Performance',
    href: '/barratt/supplier-performance',
    icon: BarChart3,
    // Built from the monthly supplier uploads, so it follows that module's switch —
    // not spend_dashboard, which only exists to show the separate Spend Dashboard item.
    featureFlag: 'supplier_uploads' as FeatureFlag,
  },
  calendarCustomer: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'calendar',
  },
  purchasing: {
    name: 'Purchasing',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchasing',
    tourId: 'purchasing',
  },
  partsMaster: {
    name: 'Parts Master',
    href: '/parts-master',
    icon: Package,
    visibilityKey: 'partsMaster' as keyof MenuVisibility,
  },
  partSchedule: {
    name: 'Part Schedule',
    href: '/part-schedule',
    icon: CalendarClock,
    visibilityKey: 'partSchedule' as keyof MenuVisibility,
    // Isolated ERP module — a tenant whose ERP feed has no part schedule can
    // switch it off via CompanySettings.feature_flags.part_schedule = false.
    // Installs that never set the key keep the page (explicit false only).
    hiddenWhenFlagFalse: 'part_schedule',
  },
  qualityCustomer: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  suppliers: {
    name: 'Suppliers',
    flipName: 'Customers',
    href: '/suppliers',
    icon: Users,
    visibilityKey: 'suppliers',
    tourId: 'suppliers',
  },
  onboarding: {
    name: 'Supplier Onboarding',
    flipName: 'Customer Onboarding',
    href: '/onboarding',
    icon: UserPlus,
    visibilityKey: 'onboarding' as keyof MenuVisibility,
  },
  userInvitationsCustomer: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  userActivityHistory: {
    name: 'User Activity History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
    // CR-6084: audit-trail surface. Hidden from non-admin users — the
    // backend endpoint enforces IsHelixOrCompanyAdmin, so a non-admin who
    // hits the route directly gets 403, but we also hide the nav item so
    // the surface isn't even discoverable.
    adminOnly: true,
  },
  questionnaire: {
    name: 'Questionnaires',
    href: '/questionnaires',
    icon: ClipboardList,
    visibilityKey: 'questionnaire',
  },
  newsReleaseCustomer: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'newsRelease',
  },
  deliveryAppeals: {
    name: 'Delivery Appeals',
    href: '/delivery-appeals',
    icon: AlertTriangle,
    featureFlag: 'delivery_appeals' as FeatureFlag,
  },
  spendLeakage: {
    name: 'Spend Leakage',
    href: '/spend-leakage',
    icon: TrendingDown,
    featureFlag: 'spend_leakage' as FeatureFlag,
  },
  dataImport: {
    name: 'Data Import',
    href: '/data-import',
    icon: Database,
    visibilityKey: 'dataImport' as keyof MenuVisibility,
  },
  importMonitor: {
    name: 'Import Monitor',
    href: '/import-monitor',
    visibilityKey: 'importMonitor' as keyof MenuVisibility,
    icon: Upload,
  },
  settings: {
    name: 'My Account',
    href: '/settings',
    icon: Settings,
  },
  // ---- Standard supplier ----
  myOnboarding: {
    name: 'My Onboarding',
    href: '/onboarding/my-progress',
    icon: UserPlus,
    visibilityKey: 'supplierOnboarding',
    tourId: 'my-onboarding',
  },
  supplierQuestionnaires: {
    name: 'Questionnaires',
    // Distinct route from "My Onboarding" (/onboarding/my-progress). Both items
    // previously shared the my-progress route, which made the sidebar highlight
    // both at once (isActive() matches on pathname) and always render the same
    // page. "Questionnaires" now opens the dedicated /questionnaires view so the
    // two menu items are independent destinations.
    href: '/questionnaires',
    icon: ClipboardList,
    visibilityKey: 'supplierQuestionnaires' as keyof MenuVisibility,
  },
  salesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchaseOrders',
    tourId: 'purchasing',
  },
  // Suppliers are invited to RFQs by notification/email but had no way to
  // get back to them — the RFQ surface is a Purchasing tab on the customer
  // side only. Give the supplier sidebar its own entry (same /rfq route,
  // which is already supplier-scoped server-side).
  rfqSupplier: {
    name: 'RFQs',
    href: '/rfq',
    icon: ClipboardList,
    visibilityKey: 'rfq',
  },
  qualitySupplier: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  customers: {
    name: 'Customers',
    href: '/customers',
    icon: Users,
    visibilityKey: 'supplierCustomers' as keyof MenuVisibility,
    tourId: 'customers',
  },
  calendarSupplier: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'supplierCalendar' as keyof MenuVisibility,
  },
  userInvitationsSupplier: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  companyProfile: {
    name: 'Company Profile',
    href: '/suppliers',
    icon: Factory,
    visibilityKey: 'companyProfile',
  },
  newsReleaseSupplier: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'supplierNewsRelease' as keyof MenuVisibility,
  },
  reportsSupplier: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  stockLevels: {
    name: 'Stock Levels',
    href: '/stock-levels',
    icon: Box,
    featureFlag: 'supplier_stock_levels' as FeatureFlag,
  },

  // ---- Barratt customer ----
  barrattCategory: {
    name: 'Category',
    href: '/barratt/category',
    icon: FolderOpen,
    children: [
      {
        name: 'Spend Data',
        href: '/barratt/spend-data',
        icon: TrendingDown,
      },
    ],
  },
  barrattSupplier: {
    name: 'Supplier',
    href: '/suppliers',
    icon: Building2,
    visibilityKey: 'suppliers',
  },
  barrattDivisionPerformance: {
    name: 'Division Performance',
    href: '/barratt/division-performance',
    icon: BarChart3,
    featureFlag: 'division_scoring' as FeatureFlag,
  },
  barrattDivisionScoreReport: {
    name: 'Division Score Report',
    href: '/barratt/division-score-report',
    icon: Table,
    featureFlag: 'division_scoring' as FeatureFlag,
  },
  barrattCallOffPerformance: {
    name: 'Call Off Performance',
    href: '/barratt/call-off-performance',
    icon: LineChart,
    featureFlag: 'call_off_performance' as FeatureFlag,
  },
  /** Monthly supplier upload cycle (no ERP feed): customer-side upload and
   *  chase surfaces. Flag-gated so tenants without the cycle never see them. */
  barrattDataUploadCustomer: {
    name: 'Data Upload',
    href: '/barratt/data-upload',
    icon: Database,
    featureFlag: 'supplier_uploads' as FeatureFlag,
  },
  /** One sidebar item, several pages (construction tenant): the pairs that
   *  are two views of one thing sit behind a tab strip (PageSwitch). The old
   *  routes redirect, so links and notifications keep working. */
  spend: {
    name: 'Spend',
    href: '/spend',
    icon: TrendingDown,
    featureFlag: 'spend_leakage' as FeatureFlag,
  },
  divisionScoring: {
    name: 'Division Scoring',
    href: '/division-scoring',
    icon: Table,
    featureFlag: 'division_scoring' as FeatureFlag,
  },
  monthlyFiles: {
    name: 'Monthly Files',
    href: '/monthly-files',
    icon: Database,
    featureFlag: 'supplier_uploads' as FeatureFlag,
  },
  /** Phase 2 (construction): supplier audits + actions, the SLA lead-time
   *  register, and invoice lines against contract rates. Menu-toggle gated
   *  (Platform Settings → Platform), customer side. */
  audits: {
    name: 'Audits',
    href: '/audits',
    icon: ClipboardCheck,
    visibilityKey: 'audits' as keyof MenuVisibility,
  },
  leadTimes: {
    name: 'Lead Times',
    href: '/lead-times',
    icon: Timer,
    visibilityKey: 'leadTimes' as keyof MenuVisibility,
  },
  priceVariance: {
    name: 'Price Variance',
    href: '/price-variance',
    icon: Receipt,
    visibilityKey: 'priceVariance' as keyof MenuVisibility,
  },
  contracts: {
    name: 'Contracts',
    href: '/contracts',
    icon: FileSignature,
    visibilityKey: 'contracts' as keyof MenuVisibility,
  },
  /** Supplier side: the audits raised on this supplier and the lead times it is measured against. */
  supplierAudits: {
    name: 'Audits',
    href: '/audits',
    icon: ClipboardCheck,
    visibilityKey: 'supplierAudits' as keyof MenuVisibility,
  },
  supplierLeadTimes: {
    name: 'My Lead Times',
    href: '/lead-times',
    icon: Timer,
    visibilityKey: 'supplierLeadTimes' as keyof MenuVisibility,
  },
  supplierContracts: {
    name: 'Contracts',
    href: '/contracts',
    icon: FileSignature,
    visibilityKey: 'supplierContracts' as keyof MenuVisibility,
  },
  barrattAnalyse: {
    name: 'Analyse',
    href: '/barratt/analyse',
    icon: TrendingDown,
  },
  barrattUserLoginHistory: {
    name: 'User Login History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
  },
  barrattSupplierUploadHistory: {
    name: 'Supplier Upload History',
    href: '/barratt/supplier-upload-history',
    icon: History,
    featureFlag: 'supplier_uploads' as FeatureFlag,
  },

  // ---- Barratt supplier ----
  barrattCustomer: {
    name: 'Customer',
    href: '/customers',
    icon: Users,
    visibilityKey: 'customers',
  },
  barrattSalesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: Truck,
    visibilityKey: 'purchaseOrders',
  },
  barrattNCRs: {
    name: 'NCRs',
    href: '/quality',
    icon: AlertOctagon,
    visibilityKey: 'quality',
  },
  /** Supplier side of the monthly upload cycle: their own Supplier
   *  Performance + Quality obligations, one card per period. */
  barrattMyUploads: {
    name: 'Monthly Uploads',
    href: '/barratt/my-uploads',
    icon: Upload,
    featureFlag: 'supplier_uploads' as FeatureFlag,
  },

  // ---- Logistics ----
  logisticsCallOffs: {
    name: 'Dispatched Call-Offs',
    href: '/logistics/call-offs',
    icon: Truck,
  },
} satisfies Record<string, Omit<NavItem, 'children'> & { children?: Omit<NavItem, 'children'>[] }>;

export type NavKey = keyof typeof NAV_ITEMS;

/** The route a menu key opens, or undefined for an unknown key. */
export function navHref(key: string): string | undefined {
  const item = (NAV_ITEMS as Record<string, NavItem>)[key];
  return item?.href;
}

// ---------------------------------------------------------------------------
// Ordered sequences per mode
// ---------------------------------------------------------------------------
// Each array lists the registry keys in the exact display order that the
// old hardcoded arrays used. Changing the order here changes the sidebar.
// ---------------------------------------------------------------------------

const STANDARD_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'supplierPerformance',
  'barrattSupplierPerformance',
  // Construction tenant, flag-gated: Spend (leakage + price variance),
  // Division Scoring (scores + report), Monthly Files (suppliers' + invoice).
  'spend',
  'divisionScoring',
  'spendDashboard',
  'barrattCallOffPerformance',
  'reportsCustomer',
  'purchasing',
  'contracts',
  'partsMaster',
  'partSchedule',
  'suppliers',
  'onboarding',
  'questionnaire',
  'audits',
  'calendarCustomer',
  'qualityCustomer',
  'monthlyFiles',
  'documents',
  'newsReleaseCustomer',
  'userActivityHistory',
  'dataImport',
  'importMonitor',
];

const STANDARD_SUPPLIER_ORDER: NavKey[] = [
  'dashboard',
  'myOnboarding',
  'supplierQuestionnaires',
  'salesOrders',
  'barrattMyUploads',
  'rfqSupplier',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'supplierPerformance',
  'supplierAudits',
  'supplierContracts',
  'documents',
  'newsReleaseSupplier',
  'stockLevels',
];

const BARRATT_CUSTOMER_ORDER: NavKey[] = [
  'barrattCategory',
  'barrattSupplier',
  'spendLeakage',
  'barrattDivisionPerformance',
  'barrattDivisionScoreReport',
  'barrattCallOffPerformance',
  'documents',
  'barrattDataUploadCustomer',
  'barrattAnalyse',
  'barrattUserLoginHistory',
  'barrattSupplierUploadHistory',
];

const BARRATT_SUPPLIER_ORDER: NavKey[] = [
  'barrattCustomer',
  'barrattSalesOrders',
  'barrattNCRs',
  'documents',
  'barrattMyUploads',
];

// CR-4143: Compliance module — stripped to onboarding/questionnaires/reports only
const COMPLIANCE_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'onboarding',
  'suppliers',
  'questionnaire',
  'reportsCustomer',
  'documents',
];

// Compliance supplier view — supplier sees only what they need to complete
const COMPLIANCE_SUPPLIER_ORDER: NavKey[] = [
  'myOnboarding',
  'supplierQuestionnaires',
  'documents',
];

// Logistics users — same sidebar as suppliers. The Call Off sub-tab inside
// Purchasing remains dispatched/delivered-only via CallOffViewSet.get_queryset()
// in the backend, so no separate nav entry is needed.
const LOGISTICS_ORDER: NavKey[] = [
  'dashboard',
  'myOnboarding',
  'supplierQuestionnaires',
  'salesOrders',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'documents',
  'newsReleaseSupplier',
  'stockLevels',
];


// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export type PortalMode = 'customer' | 'supplier';

export interface BuildNavigationOptions {
  /** Which portal the user sees: customer or supplier */
  portalMode: PortalMode;
  /** CR-5334: Whether the Spend Analytics navigation layout is active
   *  (renamed from isBarratt — the neutral module name). */
  isSpendAnalytics: boolean;
  /** Whether the application is running in Compliance mode (CR-4143) */
  isCompliance?: boolean;
  /** Whether supplier is in Customer Portal owner mode (managing customers) */
  isCustomerPortalOwner?: boolean;
  /** Whether the user belongs to a logistics company (restricted view) */
  isLogistics?: boolean;
  /** Configured order for this portal (NAV_ITEMS keys). Listed keys come first
   *  in this order; unlisted keys keep their default position after them. */
  menuOrder?: string[];
}

/** Reorder a default nav order by a configured key list; unknown keys are ignored. */
export function applyMenuOrder(order: NavKey[], menuOrder?: string[]): NavKey[] {
  if (!menuOrder || menuOrder.length === 0) return order;
  const allowed = new Set<string>(order);
  const listed: NavKey[] = [];
  for (const key of menuOrder) {
    if (allowed.has(key) && !listed.includes(key as NavKey)) listed.push(key as NavKey);
  }
  return [...listed, ...order.filter((k) => !listed.includes(k))];
}

/** The default order for a portal / mode, as `{ key, name }` rows for the settings screens. */
export interface NavOrderRow {
  key: NavKey;
  name: string;
  visibilityKey?: keyof MenuVisibility;
  supplierVisibilityKey?: keyof MenuVisibility;
  featureFlag?: FeatureFlag;
}

export function describeDefaultOrder(options: Omit<BuildNavigationOptions, 'menuOrder'>): NavOrderRow[] {
  return resolveOrder(options).map((key) => {
    const item = NAV_ITEMS[key] as NavItem & { supplierVisibilityKey?: keyof MenuVisibility };
    return {
      key,
      name: item.name,
      visibilityKey: item.visibilityKey,
      supplierVisibilityKey: item.supplierVisibilityKey,
      featureFlag: item.featureFlag,
    };
  });
}

function resolveOrder({ portalMode, isSpendAnalytics, isCompliance, isLogistics }: Omit<BuildNavigationOptions, 'menuOrder'>): NavKey[] {
  if (isLogistics) return LOGISTICS_ORDER;
  if (isCompliance) return portalMode === 'supplier' ? COMPLIANCE_SUPPLIER_ORDER : COMPLIANCE_CUSTOMER_ORDER;
  if (isSpendAnalytics) return portalMode === 'supplier' ? BARRATT_SUPPLIER_ORDER : BARRATT_CUSTOMER_ORDER;
  return portalMode === 'supplier' ? STANDARD_SUPPLIER_ORDER : STANDARD_CUSTOMER_ORDER;
}

/**
 * Returns the ordered list of `NavItem` objects for the given combination of
 * portal mode and Barratt flag.
 *
 * This replaces the four hardcoded arrays that previously lived in
 * `Sidebar.tsx`: `customerNavigation`, `supplierNavigation`,
 * `barrattCustomerNavigation`, and `barrattSupplierNavigation`.
 */
export function buildNavigation({ portalMode, isSpendAnalytics, isCompliance, isCustomerPortalOwner, isLogistics, menuOrder }: BuildNavigationOptions): NavItem[] {
  const order = applyMenuOrder(resolveOrder({ portalMode, isSpendAnalytics, isCompliance, isLogistics }), menuOrder);

  return order.map(key => {
    const item = NAV_ITEMS[key];
    const navItem: NavItem = { ...item };
    if (isCustomerPortalOwner && navItem.flipName) {
      navItem.name = navItem.flipName;
    }
    if ('children' in item && item.children) {
      navItem.children = item.children.map(child => ({ ...child }));
    }
    return navItem;
  });
}
